import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Heading, { H4, H3 } from 'components/atoms/Heading';
import Link from 'components/atoms/Link';
import Icon from 'components/atoms/Icon';
import {
  clock,
  marker,
  minus,
  plus,
  chevronDown,
  calendar,
} from 'components/atoms/Icon/icons';
import EventTicketInfo from 'components/molecules/EventTicketInfo';
import Table, { Row, Column } from 'components/molecules/TableList';
import Button, { IconButton } from 'components/atoms/Button';
import BreakpointContainer from 'containers/BreakpointContainer';
import Toggle from 'components/utilities/Toggle';
import TicketAvailabilityFetcher from 'components/utilities/TicketAvailabilityFetcher';
import getProductUrl from 'utils/getProductUrl';
import { translate } from 'utils/translate';
import { getFullUrl, appendQueryParams, parseUrl } from 'utils/bundle-helper';
import useSiteSettings from 'hooks/use-site-settings';
import { getDateFromISO, getFormattedDate } from 'utils/dateTime';

import * as styles from './ProductEventList.scss';

const grouper = (groups, item) => {
  const itemKey = item.month;
  return {
    ...groups,
    [itemKey]: [...(groups[itemKey] || []), ...[item]],
  };
};
const sortFunctions = {
  noFromTime: (a, b) => {
    if (!a.fromTime) return -1;
    return 0;
  },
  timestamp: (a, b) => {
    if (a.timestamp > b.timestamp) {
      return 1;
    }
    if (a.timestamp < b.timestamp) {
      return -1;
    }
    return 0;
  },
};

const groupProducts = products => {
  const groupedObject = products.reduce(grouper, {});
  return Object.keys(groupedObject).map(key => ({
    title: key,
    products: groupedObject[key],
  }));
};

const makeProductUrl = product => {
  if (!!product.startTime) {
    return appendQueryParams(parseUrl(getFullUrl(product.url)), {
      startDate: product.fromDate,
      startTime: getFormattedDate(product.fromTime, 'HH:mm'),
    }).href;
  }
  return appendQueryParams(parseUrl(getFullUrl(product.url)), {
    startDate: product.fromDate,
  }).href;
};

const EventListItems = ({ products, showProductLink, ticketInfo }) =>
  products.map((product, i) => (
    <React.Fragment key={product.key}>
      <Row className={styles.eventItem}>
        <Column>
          <article>
            {showProductLink ? (
              <Link href={makeProductUrl(product)} className={styles.title}>
                <H4 size={Heading.sizes.heading5}>{product.title}</H4>
              </Link>
            ) : (
              <span className={styles.title}>
                <H4 size={Heading.sizes.heading5}>{product.title}</H4>
              </span>
            )}
            <span className={styles.date}>
              <Icon
                icon={calendar}
                className={styles.timeIcon}
                size={['1.5em', '1.5em']}
              />
              <span>

                {`${getFormattedDate(
                  product.fromDate,
                  'dd',
                )} ${getFormattedDate(product.fromDate, 'MMM yyyy')}`}{' '}
                {!!product.toDate && product.fromDate !== product.toDate && (
                  <React.Fragment>
                    {"- "}
                    {getFormattedDate(product.toDate, 'dd')}
                    {getFormattedDate(product.toDate, ' MMM yyyy')}
                  </React.Fragment>
                )}
              </span>
            </span>
            <span className={styles.date}>
            <Icon
                icon={clock}
                className={styles.timeIcon}
                size={['1.5em', '1.5em']}
              />
              <span>
              <span className={styles.weekday}>
                  {product.nameOfStartDay}{' '}
                </span>
                {product.fromDate !== product.toDate && (product.nameOfEndDay !== null && product.nameOfEndDay !== '' && product.nameOfEndDay !== undefined) && (
                  <span>-&nbsp;{product.nameOfEndDay}</span>
                )}
                 {!!product.fromTime && (
                  <span>
                    {getFormattedDate(product.fromTime, ' p')}
                    {!!product.toTime &&
                      product.fromTime !== product.toTime &&
                      getFormattedDate(product.toTime, '-p')}
                  </span>
                )}
              </span>
            </span>
            <span>
              <Icon
                icon={marker}
                className={styles.iconCircle}
                size={['1.5em', '1.5em']}
              />
              <span>{product.intro}</span>
            </span>
            <div className={styles.ticketInfo}>
              <EventTicketInfo
                isLoading={ticketInfo.isLoading}
                externalData={ticketInfo.ticketData[i]}
                product={product}
                alt
              />
            </div>
          </article>
        </Column>
      </Row>
    </React.Fragment>
  ));

const alterGroupTitleByCulture = (title, culture) => {
  let out = title;
  if (culture === 'it') {
    out = title.split(' ');
    return `${out[0]}, ${out[1]} ${out[2]} ${out[3]}`;
  }
  if (culture === 'de') {
    out = title.split(',');
    // return `${out[0]}, ${out[1]} ${out[2]}`;
    return `${out[0]}, ${out[1]}`;
  }
  return out;
};

const EventListHeader = ({ columnCount, title, onClick, on }) => {
  const { culture } = useSiteSettings();

  return (
    <Row>
      <Column colSpan={columnCount}>
        <div>
          {!onClick ? (
            <H3 alt size={Heading.sizes.heading3} className={styles.groupTitle}>
              {alterGroupTitleByCulture(title, culture)}
            </H3>
          ) : (
            <Button
              onClick={onClick}
              transparent
              full
              noPadding
              className={classnames(styles.groupTitle, {
                [styles.on]: on,
              })}
            >
              <H3 alt size={Heading.sizes.heading3}>
                {title}
              </H3>
              <Icon size={['2em', '2em']} icon={on ? minus : plus} />
            </Button>
          )}
        </div>
      </Column>
    </Row>
  );
};

const ProductEventList = React.memo(
  ({
    dateVariant,
    products,
    showProductLink,
    channel,
    render,
    toggleGroups,
    groupByMonth,
    showInitial,
    title,
  }) => {
    const { productPageLink, tellusTicketChannelName } = useSiteSettings();
    const transformedProducts = useMemo(
      () =>
        products
          ? products.map(product => {
              const eventDate = getDateFromISO(
                product.fromDate,
                product.fromTime,
              );
              const timestamp = getFormattedDate(eventDate, 't');
              const now = new Date();
              const day = getFormattedDate(product.fromDate, 'PPPP');
              const month = getFormattedDate(eventDate < now ? now : product.fromDate, 'MMMM yyyy');
              const key = `${product.id}_${getFormattedDate(
                eventDate,
                "yyyy-MM-dd'T'HH:mm",
              )}`;
              const url = getProductUrl(product.url, productPageLink);
              return {
                ...product,
                eventDate,
                key,
                day,
                month,
                timestamp,
                url,
              };
            })
          : null,
      [products, productPageLink],
    );

    const sortedProducts = transformedProducts
      ? transformedProducts.sort(sortFunctions.timestamp)
      : [];

    // no products
    if (!products || !products.length) {
      return <p>{translate('/pages/eventListPage/noResult')}</p>;
    }

    if (!groupByMonth) {
      const ticketAvailabilityFetcherKey = `${sortedProducts[0].fromDate}–${sortedProducts[0].toDate}`;

      return (
        <TicketAvailabilityFetcher
          products={sortedProducts}
          channel={channel || tellusTicketChannelName}
          key={ticketAvailabilityFetcherKey}
        >
          {ticketInfo => (
            <Toggle
              on={products.length <= 3}
              render={({ on, setOn }) => (
                <React.Fragment>
                  <Table caption={title}>
                    <EventListItems
                      products={
                        on
                          ? sortedProducts
                          : sortedProducts.slice(0, showInitial)
                      }
                      dateVariant={dateVariant}
                      ticketInfo={ticketInfo}
                      showProductLink={showProductLink}
                    />
                  </Table>
                  {!on && (
                    <p className={styles.showMore}>
                      <IconButton
                        onClick={setOn}
                        iconAfter={chevronDown}
                        className={styles.showMoreDates}
                      >
                        {translate('/product/showMoreDates')}
                      </IconButton>
                    </p>
                  )}
                </React.Fragment>
              )}
            />
          )}
        </TicketAvailabilityFetcher>
      );
    }

    // grouped products
    const groupedProducts = groupProducts(sortedProducts);

    if (render) {
      return render(groupedProducts);
    }
    const ticketAvailabilityFetcherKey = `${sortedProducts[0].fromDate}–${sortedProducts[0].toDate}`;
    return (
      <Table className={styles.productEventList}>
        {groupedProducts.map((group, groupIndex) => (
          <Toggle
            key={`${group.groupTitle}`}
            on={
              (groupIndex < 2 &&
                groupedProducts[groupIndex].products.length < 5) ||
              groupIndex === 0
            }
            render={({ on, toggle }) => (
              <React.Fragment>
                {!!group.title && (
                  <EventListHeader
                    columnCount={3}
                    title={group.title}
                    onClick={toggleGroups && toggle}
                    on={on}
                  />
                )}
                {on && (
                  <TicketAvailabilityFetcher
                    products={group.products}
                    channel={channel || tellusTicketChannelName}
                    key={ticketAvailabilityFetcherKey}
                  >
                    {ticketInfo => (
                      <EventListItems
                        products={group.products}
                        dateVariant={dateVariant}
                        showProductLink={showProductLink}
                        ticketInfo={ticketInfo}
                      />
                    )}
                  </TicketAvailabilityFetcher>
                )}
              </React.Fragment>
            )}
          />
        ))}
      </Table>
    );
  },
);
ProductEventList.displayName = 'ProductEventList';
ProductEventList.propTypes = {
  products: PropTypes.array.isRequired,
  showProductLink: PropTypes.bool,
  channel: PropTypes.string,
  render: PropTypes.func,
};
ProductEventList.defaultProps = {
  dateVariant: 'dateTime',
  showProductLink: true,
  render: null,
};

export default ProductEventList;
